import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShareNetwork, Crown, ArrowRight, Flame } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { cloudyLogo } from "../lib/cloudy";

/**
 * Social-proof widget: top N businesses shared by visitors in the last 7 days.
 * Hidden when no data is available (no shares yet) — avoids empty state noise.
 */
export default function MostSharedWidget({ days = 7, limit = 5 }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/public/most-shared", { params: { days, limit } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [days, limit]);

  if (!items || items.length === 0) return null;

  return (
    <section className="border-t border-border bg-background" data-testid="most-shared-widget">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="label-uppercase mb-3 inline-flex items-center gap-2">
              <Flame weight="fill" size={14} className="text-secondary"/>
              Les plus partagés du 68
            </div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">
              Le bouche-à-oreille local en temps réel
            </h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Les commerces les plus partagés par les habitants cette semaine, via le concierge IA.
            </p>
          </div>
          <Link
            to="/annuaire"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
            data-testid="most-shared-see-all"
          >
            Explorer l'annuaire <ArrowRight size={14} />
          </Link>
        </div>

        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="most-shared-grid">
          {items.map((b, i) => {
            const isPremium = b.premium?.tier === "premium" || b.premium?.tier === "premium_ai";
            return (
              <li key={b.id || b.slug}>
                <Link
                  to={`/entreprise/${b.slug}`}
                  className="relative block bg-card border border-border rounded-sm p-4 h-full lift-card hover:border-primary transition-colors"
                  data-testid={`most-shared-item-${i}`}
                >
                  <div className="absolute -top-2 -left-2 w-7 h-7 rounded-full bg-secondary text-secondary-foreground grid place-items-center font-display font-bold text-sm shadow-md">
                    {i + 1}
                  </div>
                  <div className="flex items-start justify-between gap-2 mb-2 mt-1 pl-4">
                    <div className="font-display text-sm font-semibold leading-tight line-clamp-2">{b.name}</div>
                    {isPremium && (
                      <span className="shrink-0 inline-flex items-center gap-1 bg-primary text-primary-foreground text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm">
                        <Crown size={9} weight="fill" />
                      </span>
                    )}
                  </div>
                  {b.logo && (
                    <div className="mb-2">
                      <img
                        src={cloudyLogo(b.logo)}
                        alt={b.name}
                        className="w-10 h-10 object-cover rounded-sm border border-border"
                      />
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground mb-3 line-clamp-2">
                    {b.city}{b.category ? ` · ${b.category}` : ""}
                  </div>
                  <div className="mt-auto flex items-center gap-1.5 text-xs font-bold text-secondary tabular-nums">
                    <ShareNetwork size={12} weight="fill" />
                    {b.shares} {b.shares > 1 ? "partages" : "partage"}
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>

        {/* Shopkeeper CTA — leverages social pressure of the ranking above */}
        <div
          className="mt-8 flex items-center justify-between gap-4 flex-wrap bg-gradient-to-r from-accent/40 to-secondary/10 border border-border rounded-sm px-5 py-4"
          data-testid="most-shared-cta"
        >
          <div className="flex-1 min-w-[240px]">
            <div className="font-display text-base sm:text-lg font-medium leading-tight">
              Votre fiche ici la semaine prochaine&nbsp;?
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Les partenaires Premium remontent en tête de l'annuaire — et sont typiquement 3× plus partagés.
            </div>
          </div>
          <Link
            to="/passez-partenaire"
            className="shrink-0 inline-flex items-center gap-2 bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2.5 text-xs font-semibold rounded-sm transition-colors whitespace-nowrap"
            data-testid="most-shared-cta-btn"
          >
            Passer Premium <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}
